<!-- ── Hero metrics ─────────────────────────────────────── -->
<div class="dash-hero">
  <div class="dash-hero-tile">
    <span class="dash-hero-icon dash-ic-accent"><span [innerHTML]="'users' | icon"></span></span>
    <div>
      <div class="dash-hero-label">{{ 'dash.totalplayers' | translate }}</div>
      <div class="dash-hero-value">{{ isLoading() ? '—' : fmt(totalPlayers()) }}</div>
    </div>
  </div>
  <div class="dash-hero-tile">
    <span class="dash-hero-icon dash-ic-info"><span [innerHTML]="'games' | icon"></span></span>
    <div>
      <div class="dash-hero-label">{{ 'dash.activegames' | translate }}</div>
      <div class="dash-hero-value">
        {{ isLoading() ? '—' : fmt(activeGames()) }}
        <span class="dash-hero-of">/ {{ fmt(totalGames()) }}</span>
      </div>
    </div>
  </div>
  <div class="dash-hero-tile">
    <span class="dash-hero-icon dash-ic-gold"><span [innerHTML]="'trophy' | icon"></span></span>
    <div>
      <div class="dash-hero-label">{{ 'dash.ongoingchallenges' | translate }}</div>
      <div class="dash-hero-value">{{ isLoading() ? '—' : fmt(challengeCounts().ongoing) }}</div>
    </div>
  </div>
  <div class="dash-hero-tile">
    <span class="dash-hero-icon" [class.dash-ic-danger]="moderationCounts().pending > 0" [class.dash-ic-success]="moderationCounts().pending === 0">
      <span [innerHTML]="'shield' | icon"></span>
    </span>
    <div>
      <div class="dash-hero-label">{{ 'dash.pendingreports' | translate }}</div>
      <div class="dash-hero-value" [class.dash-text-danger]="moderationCounts().pending > 0">
        {{ isLoading() ? '—' : fmt(moderationCounts().pending) }}
      </div>
    </div>
  </div>
  <div class="dash-hero-tile">
    <span class="dash-hero-icon dash-ic-accent"><span [innerHTML]="'gift' | icon"></span></span>
    <div>
      <div class="dash-hero-label">{{ 'dash.rewards' | translate }}</div>
      <div class="dash-hero-value">{{ isLoading() ? '—' : fmt(totalRewards()) }}</div>
    </div>
  </div>
</div>

<!-- ── Primary section: players + top games ────────────────── -->
<div class="dash-grid">

  <div class="card dash-span-5 dash-card-flex">
    <div class="card-head">
      <span class="card-head-icon"><span [innerHTML]="'users' | icon"></span></span>
      <h3>{{ 'dash.playerbreakdown' | translate }}</h3>
      <div class="spacer"></div>
      <a class="page-sub" routerLink="/players">{{ 'dash.viewall' | translate }}</a>
    </div>
    <div class="card-body dash-pb-body">

      <div class="dash-pb-top">
        <div>
          <div class="dash-pb-pct">{{ playerActivePct() }}<span class="dash-pb-pct-sign">%</span></div>
          <div class="dash-pb-pct-label">{{ 'dash.active' | translate }} {{ 'dash.players_unit' | translate }}</div>
        </div>
        <div class="dash-pb-total">
          <div class="dash-pb-total-num">{{ fmt(totalPlayers()) }}</div>
          <div class="dash-pb-total-label">{{ 'dash.totalplayers' | translate }}</div>
        </div>
      </div>

      <div class="dash-pb-track">
        <div class="dash-pb-fill-active" [style.width.%]="playerActivePct()"></div>
        <div class="dash-pb-fill-suspended" [style.width.%]="100 - playerActivePct()"></div>
      </div>

      <div class="dash-pb-grid">
        @for (isActive of playerGridSquares(); track $index) {
          <span class="dash-pb-square" [class.dash-pb-square-active]="isActive" [class.dash-pb-square-suspended]="!isActive"></span>
        }
      </div>

      <div class="dash-gauge-rows">
        <a class="dash-gauge-row" routerLink="/players">
          <span class="dash-gauge-row-dot" style="background:var(--accent)"></span>
          <span class="dash-gauge-row-label">{{ 'dash.active' | translate }}</span>
          <span class="dash-gauge-row-value">{{ fmt(activePlayers()) }}</span>
          <span class="dash-gauge-row-pct">{{ playerActivePct() }}%</span>
        </a>
        <a class="dash-gauge-row" routerLink="/players">
          <span class="dash-gauge-row-dot" style="background:var(--danger)"></span>
          <span class="dash-gauge-row-label">{{ 'dash.suspended' | translate }}</span>
          <span class="dash-gauge-row-value">{{ fmt(suspendedPlayers()) }}</span>
          <span class="dash-gauge-row-pct">{{ 100 - playerActivePct() }}%</span>
        </a>
        <div class="dash-gauge-row dash-gauge-row-total">
          <span class="dash-gauge-row-dot" style="background:var(--text-faint)"></span>
          <span class="dash-gauge-row-label">{{ 'dash.totalplayers' | translate }}</span>
          <span class="dash-gauge-row-value">{{ fmt(totalPlayers()) }}</span>
        </div>
      </div>

    </div>
  </div>

  <div class="card dash-span-7">
    <div class="card-head">
      <h3>{{ 'dash.topgames' | translate }}</h3>
      <div class="spacer"></div>
      <a class="page-sub" routerLink="/games">{{ 'dash.viewall' | translate }}</a>
    </div>
    <div class="card-body dash-gamerank-list">
      @for (g of topGames(); track g._id; let i = $index) {
        <div class="dash-gamerank dash-tip-rich" [class.dash-gamerank-lead]="i === 0" [style.animation-delay.ms]="i * 70">
          <div class="dash-gamerank-ring" [class]="'dash-bar-c' + (i % 5)"
               [style.--pct.%]="((g.totalPlays || 0) / maxGamePlays()) * 100"
               [style.animation-delay.ms]="i * 70 + 150">
            <div class="dash-gamerank-thumb">
              @if (g.image) { <img [src]="gameImageUrl(g.image)" alt="" class="dash-gamerank-img"> }
              @else { <span class="dash-gamerank-fallback">{{ initials(gameName(g)) }}</span> }
            </div>
            <span class="dash-gamerank-rank">{{ i + 1 }}</span>
          </div>

          <div class="dash-gamerank-info">
            <span class="dash-gamerank-name">{{ gameName(g) }}</span>
            <span class="dash-gamerank-sub">{{ 'dash.plays_unit' | translate }}</span>
          </div>

          <div class="dash-gamerank-value">
            <span class="dash-gamerank-value-num">{{ fmt(g.totalPlays || 0) }}</span>
            <svg class="dash-gamerank-spark" viewBox="0 0 60 20" preserveAspectRatio="none">
              <polyline [class]="'dash-bar-c' + (i % 5)" points="0,16 10,12 20,14 30,7 40,9 50,3 60,5"></polyline>
            </svg>
          </div>

          <div class="dash-tip-box">
            <div class="dash-tip-title">{{ gameName(g) }}</div>
            <div class="dash-tip-row">
              <span class="dash-tip-swatch" [class]="'dash-bar-c' + (i % 5)"></span>
              <span class="dash-tip-value">{{ fmt(g.totalPlays || 0) }}</span>
              <span>{{ 'dash.plays_unit' | translate }}</span>
            </div>
          </div>
        </div>
      } @empty {
        @if (!isLoading()) {
          <div class="dash-empty">{{ 'dash.nogames' | translate }}</div>
        }
      }
    </div>
  </div>

</div>

<!-- ── Secondary section: challenges, moderation, platform ─── -->
<div class="dash-grid dash-grid-secondary">

  <div class="card dash-span-4">
    <div class="card-head">
      <h3>{{ 'dash.challengestatus' | translate }}</h3>
      <div class="spacer"></div>
      <a class="page-sub" routerLink="/challenges">{{ 'dash.viewall' | translate }}</a>
    </div>
    <div class="card-body dash-donut-body">
      <svg viewBox="0 0 120 120" class="dash-donut" [class.dash-flip]="i18n.lang() === 'ar'">
        <circle cx="60" cy="60" r="54" fill="none" stroke="var(--surface-3)" stroke-width="12"/>
        @for (seg of challengeDonutSegments(); track seg.key) {
          @if (seg.value > 0) {
            <circle cx="60" cy="60" r="54" fill="none" [attr.stroke]="seg.color" stroke-width="12"
                    [attr.stroke-dasharray]="seg.dash + ' ' + (ringCircumference - seg.dash)"
                    [attr.stroke-dashoffset]="seg.offset"
                    transform="rotate(-90 60 60)"
                    class="dash-donut-ring dash-donut-seg"
                    [class.dash-donut-seg-active]="hoveredChallengeSeg() === seg.key"
                    [class.dash-donut-seg-dim]="hoveredChallengeSeg() !== null && hoveredChallengeSeg() !== seg.key"
                    (mouseenter)="hoveredChallengeSeg.set(seg.key)"
                    (mouseleave)="hoveredChallengeSeg.set(null)">
              <title>{{ (seg.label | translate) }}: {{ fmt(seg.value) }} ({{ seg.pct }}%)</title>
            </circle>
          }
        }
        <text x="60" y="56" text-anchor="middle" font-size="22" font-weight="800" fill="var(--text)" class="dash-donut-text">{{ fmt(totalChallenges()) }}</text>
        <text x="60" y="74" text-anchor="middle" font-size="9.5" fill="var(--text-muted)" class="dash-donut-text">{{ 'dash.totalchallenges' | translate }}</text>
      </svg>
      <div class="dash-legend">
        @for (seg of challengeDonutSegments(); track seg.key) {
          <div class="dash-legend-row dash-tip-rich"
               (mouseenter)="hoveredChallengeSeg.set(seg.key)"
               (mouseleave)="hoveredChallengeSeg.set(null)">
            <span class="dash-dot" [style.background]="seg.color"></span>
            <span class="dash-legend-label">{{ seg.label | translate }}</span>
            <span class="dash-legend-value">{{ fmt(seg.value) }}</span>
            <div class="dash-tip-box">
              <div class="dash-tip-title">{{ seg.label | translate }}</div>
              <div class="dash-tip-row">
                <span class="dash-tip-swatch" [style.background]="seg.color"></span>
                <span class="dash-tip-value">{{ fmt(seg.value) }}</span>
                <span>{{ 'dash.challenges_unit' | translate }} · {{ seg.pct }}%</span>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <div class="card dash-span-4">
    <div class="card-head">
      <h3>{{ 'dash.moderation' | translate }}</h3>
      <div class="spacer"></div>
      <a class="page-sub" routerLink="/moderation">{{ 'dash.viewall' | translate }}</a>
    </div>
    <div class="card-body dash-mod-body">

      <div class="dash-mod-hero">
        <div class="dash-mod-hero-ring">
          <svg viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="27" fill="none" stroke="var(--danger)" stroke-width="7"
                    transform="rotate(-90 32 32)"
                    class="dash-donut-ring"/>
            <circle cx="32" cy="32" r="27" fill="none" stroke="var(--success)" stroke-width="7"
                    stroke-linecap="round"
                    [attr.stroke-dasharray]="modResolvedCircumference"
                    [attr.stroke-dashoffset]="modResolvedOffset()"
                    transform="rotate(-90 32 32)"
                    class="dash-donut-ring"/>
          </svg>
          <span class="dash-mod-hero-pct">{{ modResolvedPct() }}%</span>
        </div>
        <div class="dash-mod-hero-text">
          <div class="dash-mod-hero-title">{{ 'dash.resolutionrate' | translate }}</div>
          <div class="dash-mod-hero-sub">{{ fmt(moderationCounts().actioned + moderationCounts().dismissed) }} {{ 'dash.of' | translate }} {{ fmt(moderationTotal()) }} {{ 'dash.resolved' | translate }}</div>
        </div>
      </div>

      <div class="dash-mod-tiles">
        <a class="dash-mod-tile dash-tip-rich" routerLink="/moderation">
          <span class="dash-mod-tile-icon dash-ic-danger"><span [innerHTML]="'flag' | icon"></span></span>
          <span class="dash-mod-tile-value">{{ fmt(moderationCounts().pending) }}</span>
          <span class="dash-mod-tile-label">{{ 'dash.pending' | translate }}</span>
          <div class="dash-tip-box">
            <div class="dash-tip-title">{{ 'dash.pending' | translate }}</div>
            <div class="dash-tip-row">
              <span class="dash-tip-swatch" style="background:var(--danger)"></span>
              <span class="dash-tip-value">{{ fmt(moderationCounts().pending) }}</span>
              <span>{{ 'dash.reports_unit' | translate }}</span>
            </div>
          </div>
        </a>
        <a class="dash-mod-tile dash-tip-rich" routerLink="/moderation">
          <span class="dash-mod-tile-icon dash-ic-gold"><span [innerHTML]="'shield' | icon"></span></span>
          <span class="dash-mod-tile-value">{{ fmt(moderationCounts().actioned) }}</span>
          <span class="dash-mod-tile-label">{{ 'dash.actioned' | translate }}</span>
          <div class="dash-tip-box">
            <div class="dash-tip-title">{{ 'dash.actioned' | translate }}</div>
            <div class="dash-tip-row">
              <span class="dash-tip-swatch" style="background:var(--gold)"></span>
              <span class="dash-tip-value">{{ fmt(moderationCounts().actioned) }}</span>
              <span>{{ 'dash.reports_unit' | translate }}</span>
            </div>
          </div>
        </a>
        <a class="dash-mod-tile dash-tip-rich" routerLink="/moderation">
          <span class="dash-mod-tile-icon dash-ic-success"><span [innerHTML]="'check' | icon"></span></span>
          <span class="dash-mod-tile-value">{{ fmt(moderationCounts().dismissed) }}</span>
          <span class="dash-mod-tile-label">{{ 'dash.dismissed' | translate }}</span>
          <div class="dash-tip-box">
            <div class="dash-tip-title">{{ 'dash.dismissed' | translate }}</div>
            <div class="dash-tip-row">
              <span class="dash-tip-swatch" style="background:var(--success)"></span>
              <span class="dash-tip-value">{{ fmt(moderationCounts().dismissed) }}</span>
              <span>{{ 'dash.reports_unit' | translate }}</span>
            </div>
          </div>
        </a>
      </div>

    </div>
  </div>

  <div class="card dash-span-4">
    <div class="card-head">
      <h3>{{ 'dash.platform' | translate }}</h3>
    </div>
    <div class="card-body dash-quick-list">
      <a class="dash-quick-row" routerLink="/categories">
        <span class="dash-quick-icon"><span [innerHTML]="'category' | icon"></span></span>
        <span class="dash-quick-label">{{ 'dash.categories' | translate }}</span>
        <span class="dash-quick-value">{{ fmt(totalCategories()) }}</span>
      </a>
      <a class="dash-quick-row" routerLink="/rewards">
        <span class="dash-quick-icon"><span [innerHTML]="'gift' | icon"></span></span>
        <span class="dash-quick-label">{{ 'dash.rewards' | translate }}</span>
        <span class="dash-quick-value">{{ fmt(totalRewards()) }}</span>
      </a>
      <a class="dash-quick-row" routerLink="/challenges">
        <span class="dash-quick-icon"><span [innerHTML]="'trophy' | icon"></span></span>
        <span class="dash-quick-label">{{ 'dash.totalchallenges' | translate }}</span>
        <span class="dash-quick-value">{{ fmt(totalChallenges()) }}</span>
      </a>
      <a class="dash-quick-row" routerLink="/games">
        <span class="dash-quick-icon"><span [innerHTML]="'games' | icon"></span></span>
        <span class="dash-quick-label">{{ 'dash.totalgames' | translate }}</span>
        <span class="dash-quick-value">{{ fmt(totalGames()) }}</span>
      </a>
    </div>
  </div>

</div>

<!-- ── Tertiary section: challenges by reach + player demographics ─── -->
<div class="dash-grid dash-grid-secondary">

  <div class="card dash-span-5">
    <div class="card-head">
      <h3>{{ 'dash.topchallenges' | translate }}</h3>
      <div class="spacer"></div>
      <a class="page-sub" routerLink="/challenges">{{ 'dash.viewall' | translate }}</a>
    </div>
    <div class="card-body dash-game-list">
      @for (c of topChallenges(); track c._id) {
        <div class="dash-game-row">
          <div class="avatar dash-game-avatar">{{ initials(challengeName(c)) }}</div>
          <div class="dash-game-info">
            <div class="dash-game-top">
              <span class="dash-game-name">{{ challengeName(c) }}</span>
              <span class="dash-game-plays">{{ fmt(c.participants || 0) }}</span>
            </div>
            <div class="dash-bar-track dash-tip-rich">
              <div class="dash-bar-fill dash-bar-gold" [style.width.%]="((c.participants || 0) / maxChallengeParticipants()) * 100"></div>
              <div class="dash-tip-box">
                <div class="dash-tip-title">{{ challengeName(c) }}</div>
                <div class="dash-tip-row">
                  <span class="dash-tip-swatch dash-bar-gold"></span>
                  <span class="dash-tip-value">{{ fmt(c.participants || 0) }}</span>
                  <span>{{ 'dash.participants_unit' | translate }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      } @empty {
        @if (!isLoading()) {
          <div class="dash-empty">{{ 'dash.nochallenges' | translate }}</div>
        }
      }
    </div>
  </div>

  <div class="card dash-span-4">
    <div class="card-head">
      <h3>{{ 'dash.devicebreakdown' | translate }}</h3>
    </div>
    <div class="card-body dash-device-body">
      <div class="dash-device-list">
        @for (seg of deviceDonutSegments(); track seg.key) {
          <div class="dash-device-row">
            <span class="dash-device-icon" [style.background]="seg.color + '26'" [style.color]="seg.color">
              <span [innerHTML]="(seg.icon || 'phone') | icon"></span>
            </span>
            <div class="dash-device-main">
              <div class="dash-device-top">
                <span class="dash-device-label">{{ seg.label | translate }}</span>
                <span class="dash-device-pct">{{ seg.pct }}%</span>
              </div>
              <div class="dash-device-track">
                <div class="dash-device-fill" [style.width.%]="seg.pct" [style.background]="seg.color"></div>
              </div>
              <div class="dash-device-count">{{ fmt(seg.value) }} {{ 'dash.players_unit' | translate }}</div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <div class="card dash-span-3">
    <div class="card-head">
      <span class="card-head-icon"><span [innerHTML]="'globe' | icon"></span></span>
      <h3>{{ 'dash.topcountries' | translate }}</h3>
    </div>
    <div class="card-body dash-country-body">
      @for (c of countryCounts(); track c.name) {
        <div class="dash-country-row">
          <span class="dash-country-flag">{{ countryFlag(c.code) || '🏳' }}</span>
          <div class="dash-country-main">
            <div class="dash-country-top">
              <span class="dash-country-name">{{ c.name }}</span>
              <span class="dash-country-count">{{ fmt(c.count) }}</span>
            </div>
            <div class="dash-country-bottom">
              <div class="dash-bar-track dash-country-track">
                <div class="dash-bar-fill dash-bar-ios" [style.width.%]="(c.count / maxCountryCount()) * 100"></div>
              </div>
              <span class="dash-country-pct">{{ countryPct(c.count) }}%</span>
            </div>
          </div>
        </div>
      } @empty {
        @if (!isLoading()) {
          <div class="dash-empty">{{ 'dash.nodata' | translate }}</div>
        }
      }
    </div>
  </div>

</div>
